import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getWeekRange } from '@/lib/agenda-timeline';
import type { AgendaView } from './TimelineGrid';

interface AgendaToolbarProps {
  view: AgendaView;
  viewDate: Date;
  onViewChange: (view: AgendaView) => void;
  onDateChange: (d: Date) => void;
}

export function AgendaToolbar(props: AgendaToolbarProps) {
  const { t, i18n } = useTranslation();

  const label = (() => {
    if (props.view === 'day') {
      return props.viewDate.toLocaleDateString(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
    const { start, end } = getWeekRange(props.viewDate);
    return `${start.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  })();

  function step(delta: number) {
    const d = new Date(props.viewDate);
    d.setDate(d.getDate() + delta * (props.view === 'week' ? 7 : 1));
    props.onDateChange(d);
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex p-1 rounded-full bg-muted/60 gap-1">
        <button
          type="button"
          onClick={() => props.onViewChange('week')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            props.view === 'week'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('agenda.view.week')}
        </button>
        <button
          type="button"
          onClick={() => props.onViewChange('day')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
            props.view === 'day'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('agenda.view.day')}
        </button>
      </div>

      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => step(-1)}
          className="painel__icon-btn"
          aria-label={t('agenda.previous')}
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => props.onDateChange(new Date())}
          className="ow-btn ow-btn--outline ow-btn--sm"
        >
          {t('agenda.today')}
        </button>
        <button
          type="button"
          onClick={() => step(1)}
          className="painel__icon-btn"
          aria-label={t('agenda.next')}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <span className="text-sm font-semibold capitalize">{label}</span>
    </div>
  );
}
